import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { RRule } from 'rrule';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../styles/group/CommunityCalendar.css';
import '../../styles/group/JewishHolidays.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPlus, faFilter, faEllipsisV, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import CreateCalendarItemModal from './CreateCalendarItemModal';
import CreateCalendarModal from './CreateCalendarModal';
import EditCalendarModal from './EditCalendarModal';
import GoogleCalendarImport from './GoogleCalendarImport';
import CreateEventModal from './CreateEventModal';
import CreateClassModal from './CreateClassModal';
import calendarService from '../../services/calendarService';
import calendarApi from '../../services/calendarApi';
import groupService from '../../api/groupService';
import prayerService from '../../api/prayerService';
import jewishHolidaysService from '../../api/jewishHolidaysService';

// Feature flag: Set to true when Google authorizes the platform
const ENABLE_GOOGLE_CALENDAR_IMPORT = true;

const localizer = momentLocalizer(moment);

const CommunityCalendar = ({ groupId, isAdmin = true }) => {
  const navigate = useNavigate();
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [activeCalendars, setActiveCalendars] = useState(new Set()); // Changed to Set for multi-select
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalInitialStep, setCreateModalInitialStep] = useState('choose');
  const [showCreateCalendarModal, setShowCreateCalendarModal] = useState(false);
  const [showEditCalendarModal, setShowEditCalendarModal] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState(null);
  const [showGoogleCalendarModal, setShowGoogleCalendarModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showCreateClassModal, setShowCreateClassModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentView, setCurrentView] = useState('month'); // Default to month view
  const [currentDate, setCurrentDate] = useState(new Date());
  const [openCalendarDropdownId, setOpenCalendarDropdownId] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [createMenuPosition, setCreateMenuPosition] = useState({ x: 0, y: 0 });
  
  // Jewish Holidays state
  const [jewishHolidays, setJewishHolidays] = useState([]);
  const [selectedHolidayFilters, setSelectedHolidayFilters] = useState(() => {
    const saved = localStorage.getItem('calendarHolidayFilters');
    return saved ? JSON.parse(saved) : ['major', 'minor', 'fast', 'rosh_chodesh', 'shabbat', 'modern'];
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Fetch calendars for the group
  const fetchCalendars = useCallback(async () => {
    try {
      const response = await calendarApi.getCalendars(groupId);
      
      // The API returns { data: calendars }, and calendarApi returns response.data
      // So we need response.data (which is already the { data: calendars } object)
      let calendarsData = response.data || [];
      
      // Sort calendars: default first, then by display_order
      calendarsData = calendarsData.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return (a.display_order || 0) - (b.display_order || 0);
      });
      
      setCalendars(calendarsData);
      
      // Set active calendars - default to all calendars or just the default one
      setActiveCalendars(prev => {
        if (prev.size > 0) return prev; // Keep existing selection
        const defaultCalendar = calendarsData.find(c => c.is_default);
        return new Set(defaultCalendar ? [defaultCalendar.id] : calendarsData.map(c => c.id));
      });
    } catch (err) {
      console.error('Error fetching calendars:', err);
      console.error('Error details:', err.response?.data);
    }
  }, [groupId]); // Removed activeCalendar from dependencies

  // Load calendars on mount
  useEffect(() => {
    if (groupId) {
      fetchCalendars();
    }
  }, [groupId, fetchCalendars]);

  // Fetch Jewish holidays when calendar view changes
  useEffect(() => {
    const fetchJewishHolidays = async () => {
      console.log('=== JEWISH HOLIDAYS FETCH START ===');
      console.log('Selected Holiday Filters:', selectedHolidayFilters);
      console.log('Current View:', currentView);
      console.log('Current Date:', currentDate);
      
      if (selectedHolidayFilters.length === 0) {
        console.log('No filters selected, skipping fetch');
        setJewishHolidays([]);
        return;
      }

      try {
        let startDate, endDate;
        
        // Calculate date range based on current view
        if (currentView === 'month') {
          // For month view, get the entire month
          const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
          const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
          startDate = startOfMonth.toISOString().split('T')[0];
          endDate = endOfMonth.toISOString().split('T')[0];
        } else if (currentView === 'week') {
          // For week view, get the entire week
          const startOfWeek = new Date(currentDate);
          startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          startDate = startOfWeek.toISOString().split('T')[0];
          endDate = endOfWeek.toISOString().split('T')[0];
        } else if (currentView === 'day') {
          // For day view, get just that day
          startDate = currentDate.toISOString().split('T')[0];
          endDate = currentDate.toISOString().split('T')[0];
        }
        
        console.log('Fetching Jewish holidays:');
        console.log('  - Start Date:', startDate);
        console.log('  - End Date:', endDate);
        console.log('  - Selected Filters:', selectedHolidayFilters);
        
        // Fetch holidays for each selected filter and combine results
        const allHolidays = [];
        for (const filter of selectedHolidayFilters) {
          const holidays = await jewishHolidaysService.getHolidays(
            startDate,
            endDate,
            filter
          );
          if (holidays) {
            allHolidays.push(...holidays);
          }
        }
        
        // Remove duplicates based on id
        const holidays = Array.from(
          new Map(allHolidays.map(h => [h.id, h])).values()
        );
        
        console.log('API Response - Fetched holidays:', holidays);
        console.log('  - Type:', typeof holidays);
        console.log('  - Is Array:', Array.isArray(holidays));
        console.log('  - Count:', holidays?.length || 0);
        
        if (holidays && holidays.length > 0) {
          console.log('  - First holiday:', holidays[0]);
          console.log('  - Sample dates:', holidays.slice(0, 3).map(h => h.gregorian_date));
        } else {
          console.warn('⚠️ No holidays returned from API');
        }
        
        setJewishHolidays(holidays);
        console.log(`✅ Set ${holidays?.length || 0} holidays in state`);
      } catch (error) {
        console.error('❌ Error fetching Jewish holidays:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
      }
      
      console.log('=== JEWISH HOLIDAYS FETCH END ===\n');
    };

    fetchJewishHolidays();
  }, [currentDate, selectedHolidayFilters, currentView]); // Added currentView dependency

  // Persist holiday filters to localStorage
  useEffect(() => {
    localStorage.setItem('calendarHolidayFilters', JSON.stringify(selectedHolidayFilters));
  }, [selectedHolidayFilters]);

  // Toggle a holiday filter on/off
  const toggleHolidayFilter = (filterValue) => {
    setSelectedHolidayFilters(prev => {
      if (prev.includes(filterValue)) {
        // Remove filter
        return prev.filter(f => f !== filterValue);
      } else {
        // Add filter
        return [...prev, filterValue];
      }
    });
  };

  // Select all filters
  const selectAllFilters = () => {
    setSelectedHolidayFilters(['major', 'minor', 'fast', 'rosh_chodesh', 'shabbat', 'modern']);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedHolidayFilters([]);
  };

  // Debug: Log when jewishHolidays state changes
  useEffect(() => {
    console.log('🔄 jewishHolidays state changed:');
    console.log('  - Count:', jewishHolidays.length);
    console.log('  - Data:', jewishHolidays);
    if (jewishHolidays.length > 0) {
      console.log('  - Date range:', {
        first: jewishHolidays[0].gregorian_date,
        last: jewishHolidays[jewishHolidays.length - 1].gregorian_date
      });
    }
  }, [jewishHolidays]);

  // Helper function to get holidays for a specific date
  const getHolidaysForDate = useCallback((date) => {
    const dateStr = date.toISOString().split('T')[0];
    const filtered = jewishHolidays.filter(h => h.gregorian_date === dateStr);
    
    if (filtered.length > 0) {
      console.log(`📅 Found ${filtered.length} holiday(s) for ${dateStr}:`, filtered.map(h => h.name));
    }
    
    return filtered;
  }, [jewishHolidays]);

  // Helper function to expand recurring calendar items
  const expandRecurringItem = useCallback((item, viewStart, viewEnd) => {
    if (!item.recurrence_rule) {
      // Non-recurring item - return single instance
      const startDate = new Date(item.start_time);
      let endDate = new Date(item.end_time);
      
      // For all-day events, ensure end date is the next day (exclusive)
      if (item.all_day) {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }
      
      return [{
        id: `item-${item.id}`,
        title: item.title,
        start: startDate,
        end: endDate,
        allDay: item.all_day,
        resource: {
          type: 'other',
          data: item,
          color: item.color || '#6b7280',
          isRecurring: false
        }
      }];
    }

    try {
      // Get the original start time to preserve time-of-day
      const originalStart = new Date(item.start_time);
      const originalHours = originalStart.getHours();
      const originalMinutes = originalStart.getMinutes();
      const originalSeconds = originalStart.getSeconds();
      
      // Parse the RRULE - handle both formats (with or without DTSTART)
      let rruleString = item.recurrence_rule;
      
      // If the recurrence_rule already contains DTSTART, use it as-is
      // Otherwise, prepend DTSTART and RRULE prefix
      if (!rruleString.includes('DTSTART')) {
        const dtstart = new Date(item.start_time).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        rruleString = `DTSTART:${dtstart}\nRRULE:${rruleString}`;
      } else if (!rruleString.includes('RRULE:')) {
        // If DTSTART exists but RRULE: prefix is missing, add it
        const parts = rruleString.split('\n');
        if (parts.length === 2 && parts[0].startsWith('DTSTART')) {
          rruleString = `${parts[0]}\nRRULE:${parts[1]}`;
        }
      }
      
      console.log('Parsing RRULE for item:', item.id, rruleString);
      const rule = RRule.fromString(rruleString);
      console.log('Parsed rule:', rule.toString());
      
      // Calculate the duration of the original event
      const duration = new Date(item.end_time) - new Date(item.start_time);
      
      // Get all occurrences within the view range
      // Add some buffer (30 days before and after) to ensure we catch all relevant instances
      const bufferStart = new Date(viewStart);
      bufferStart.setDate(bufferStart.getDate() - 30);
      const bufferEnd = new Date(viewEnd);
      bufferEnd.setDate(bufferEnd.getDate() + 30);
      
      const occurrences = rule.between(bufferStart, bufferEnd, true);
      console.log(`Generated ${occurrences.length} occurrences for item ${item.id}:`, occurrences);
      
      // Convert each occurrence to a calendar event
      return occurrences.map((occurrence, index) => {
        // Preserve the original time-of-day (fix timezone issues)
        const localOccurrence = new Date(occurrence);
        localOccurrence.setHours(originalHours, originalMinutes, originalSeconds, 0);
        
        let endTime;
        if (item.all_day) {
          // For all-day events, end should be the next day (exclusive)
          endTime = new Date(localOccurrence);
          endTime.setDate(endTime.getDate() + 1);
        } else {
          endTime = new Date(localOccurrence.getTime() + duration);
        }
        
        return {
          id: `item-${item.id}-occurrence-${index}`,
          title: item.title,
          start: localOccurrence,
          end: endTime,
          allDay: item.all_day,
          resource: {
            type: 'other',
            data: item,
            color: item.color || '#6b7280',
            isRecurring: true,
            occurrenceDate: localOccurrence
          }
        };
      });
    } catch (error) {
      console.error('Error parsing recurrence rule for item:', item.id, error);
      // Fallback to single instance if parsing fails
      const startDate = new Date(item.start_time);
      let endDate = new Date(item.end_time);
      
      // For all-day events, ensure end date is the next day (exclusive)
      if (item.all_day) {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }
      
      return [{
        id: `item-${item.id}`,
        title: item.title,
        start: startDate,
        end: endDate,
        allDay: item.all_day,
        resource: {
          type: 'other',
          data: item,
          color: item.color || '#6b7280',
          isRecurring: false
        }
      }];
    }
  }, []);

  // Fetch all calendar data (events, classes, prayer times, and calendar items)
  const fetchCalendarData = useCallback(async () => {
    console.log('🔄 fetchCalendarData called - forcing fresh fetch');
    try {
      setLoading(true);
      setError(null);

      // Fetch all data sources, with individual error handling
      const [eventsResponse, classesResponse, prayerTimesResponse, calendarItemsResponse] = await Promise.allSettled([
        groupService.getGroupEvents(groupId),
        groupService.getGroupClasses(groupId),
        prayerService.getPrayerTimes(groupId).catch(err => {
          console.warn('Failed to fetch prayer times:', err);
          return { data: [] };
        }),
        calendarService.getCalendarItems(groupId).catch(err => {
          console.warn('Failed to fetch calendar items:', err);
          return { data: [] };
        })
      ]);

      // Extract data arrays from responses
      const events = eventsResponse.status === 'fulfilled' 
        ? (Array.isArray(eventsResponse.value?.data) ? eventsResponse.value.data : 
           Array.isArray(eventsResponse.value) ? eventsResponse.value : [])
        : [];
        
      const classes = classesResponse.status === 'fulfilled'
        ? (Array.isArray(classesResponse.value?.data) ? classesResponse.value.data : 
           Array.isArray(classesResponse.value) ? classesResponse.value : [])
        : [];
        
      const prayerTimes = prayerTimesResponse.status === 'fulfilled'
        ? (Array.isArray(prayerTimesResponse.value?.data) ? prayerTimesResponse.value.data : 
           Array.isArray(prayerTimesResponse.value) ? prayerTimesResponse.value : [])
        : [];
        
      const calendarItems = calendarItemsResponse.status === 'fulfilled'
        ? (Array.isArray(calendarItemsResponse.value?.data) ? calendarItemsResponse.value.data : 
           Array.isArray(calendarItemsResponse.value) ? calendarItemsResponse.value : [])
        : [];

      console.log('✅ Fetched calendar data:', {
        events: events.length,
        classes: classes.length,
        prayerTimes: prayerTimes.length,
        calendarItems: calendarItems.length
      });
      
      if (calendarItems.length > 0) {
        console.log('Sample calendar item:', calendarItems[0]);
        console.log('Calendar items with all_day:', calendarItems.filter(item => item.all_day));
      }
      
      console.log('Prayer times raw response:', prayerTimesResponse);
      console.log('Prayer times array:', prayerTimes);

      // Helper function to get the next occurrence of a day of week
      const getNextOccurrence = (dayOfWeek, time) => {
        const days = {
          'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
          'Thursday': 4, 'Friday': 5, 'Saturday': 6, 'Shabbat': 6
        };
        
        const today = new Date();
        const targetDay = days[dayOfWeek];
        const currentDay = today.getDay();
        
        // Calculate days until next occurrence
        let daysUntil = targetDay - currentDay;
        if (daysUntil < 0) daysUntil += 7;
        
        const nextDate = new Date(today);
        nextDate.setDate(today.getDate() + daysUntil);
        
        // Parse time (format: "HH:MM AM/PM" or "HH:MM")
        const [timeStr, period] = time.split(' ');
        const [hours, minutes] = timeStr.split(':').map(Number);
        
        let hour24 = hours;
        if (period === 'PM' && hours !== 12) hour24 += 12;
        if (period === 'AM' && hours === 12) hour24 = 0;
        
        nextDate.setHours(hour24, minutes, 0, 0);
        
        return nextDate;
      };

      // Transform events for calendar display
      const transformedEvents = [
        // Regular events
        ...events.map(event => {
          const eventDate = new Date(event.date || event.date_time);
          const endDate = new Date(eventDate.getTime() + 60 * 60000); // Default 1 hour duration
          
          return {
            id: `event-${event.id}`,
            title: event.title,
            start: eventDate,
            end: endDate,
            allDay: false,
            resource: {
              type: 'event',
              data: event,
              color: '#FF5963' // Red for events
            }
          };
        }),
        
        // Classes
        ...classes.map(classItem => {
          const classDate = new Date(classItem.date || classItem.date_time);
          const endDate = new Date(classDate.getTime() + 60 * 60000); // Default 1 hour duration
          
          return {
            id: `class-${classItem.id}`,
            title: classItem.title,
            start: classDate,
            end: endDate,
            allDay: false,
            resource: {
              type: 'class',
              data: classItem,
              color: '#3b82f6' // Blue for classes
            }
          };
        }),
        
        // Prayer times (display for the next 30 days)
        ...(Array.isArray(prayerTimes) ? prayerTimes.flatMap(prayer => {
          const prayerEvents = [];
          // Generate prayer time events for the next 30 days
          for (let week = 0; week < 5; week++) {
            const startDate = getNextOccurrence(prayer.day_of_week, prayer.time);
            startDate.setDate(startDate.getDate() + (week * 7));
            
            const endDate = new Date(startDate.getTime() + 30 * 60000); // 30 minutes duration
            
            prayerEvents.push({
              id: `prayer-${prayer.id}-week${week}`,
              title: `${prayer.prayer_type} - ${prayer.description}`,
              start: startDate,
              end: endDate,
              allDay: false,
              resource: {
                type: 'prayer',
                data: prayer,
                color: '#10b981' // Green for prayer times
              }
            });
          }
          return prayerEvents;
        }) : []),
        
        // Calendar items - transform them with resource wrapper for later expansion
        ...calendarItems.map(item => ({
          id: `item-${item.id}`,
          resource: {
            type: 'other',
            data: item
          }
        }))
      ];

      console.log('Total transformed events:', transformedEvents.length);
      console.log('Calendar items transformed:', calendarItems.length);

      setCalendarEvents(transformedEvents);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError(err.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [groupId, expandRecurringItem]); // Don't trigger re-fetch on view/date change

  useEffect(() => {
    if (groupId) {
      fetchCalendarData();
    }
  }, [groupId, fetchCalendarData]);

  // Expand recurring calendar items based on current view and date
  const expandedCalendarEvents = useMemo(() => {
    if (!calendarEvents || calendarEvents.length === 0) {
      return [];
    }

    // Calculate view range based on current view
    let viewStart, viewEnd;
    
    if (currentView === 'month') {
      viewStart = new Date(currentDate);
      viewStart.setDate(1); // First day of month
      viewEnd = new Date(currentDate);
      viewEnd.setMonth(viewEnd.getMonth() + 1);
      viewEnd.setDate(0); // Last day of month
    } else if (currentView === 'week') {
      // Get the start of the week
      viewStart = new Date(currentDate);
      const day = viewStart.getDay();
      viewStart.setDate(viewStart.getDate() - day); // Go to Sunday
      viewEnd = new Date(viewStart);
      viewEnd.setDate(viewEnd.getDate() + 6); // Saturday
    } else { // day view
      viewStart = new Date(currentDate);
      viewStart.setHours(0, 0, 0, 0);
      viewEnd = new Date(currentDate);
      viewEnd.setHours(23, 59, 59, 999);
    }

    // Separate calendar items from other events
    // Calendar items need to go through expandRecurringItem (even non-recurring ones)
    const regularEvents = calendarEvents.filter(event => 
      event.resource?.type !== 'other'
    );
    
    const calendarItems = calendarEvents.filter(event => 
      event.resource?.type === 'other'
    );

    console.log('Expanding calendar items:', calendarItems.length);

    // Expand calendar items (both recurring and non-recurring)
    const expandedItems = calendarItems.flatMap(item => {
      return expandRecurringItem(item.resource.data, viewStart, viewEnd);
    });

    console.log('Expanded to:', expandedItems.length, 'events');

    return [...regularEvents, ...expandedItems];
  }, [calendarEvents, currentView, currentDate, expandRecurringItem]);

  // Filter events based on active calendars - treat tabs as filters
  const filteredCalendarEvents = useMemo(() => {
    if (!activeCalendars || activeCalendars.size === 0 || !expandedCalendarEvents || expandedCalendarEvents.length === 0) {
      return expandedCalendarEvents || [];
    }

    return expandedCalendarEvents.filter(event => {
      // Extract the original data from the event resource
      const originalData = event.resource?.data;
      
      // If no original data or no calendarIds, include it (for backward compatibility)
      if (!originalData || !originalData.calendarIds) {
        return true;
      }

      // Check if this item belongs to any of the active calendars
      return originalData.calendarIds.some(calId => activeCalendars.has(calId));
    });
  }, [expandedCalendarEvents, activeCalendars]);

  // Combine calendar events with Jewish holidays for display in week/day views only
  // Month view uses MonthDateCellWrapper to show holidays, so we exclude them there
  const allCalendarEvents = useMemo(() => {
    // Start with filtered calendar events
    const events = [...(filteredCalendarEvents || [])];
    
    
    // Add Jewish holidays as all-day events ONLY for week and day views
    // Month view already shows holidays via MonthDateCellWrapper
    if (currentView !== 'month' && jewishHolidays && jewishHolidays.length > 0) {
      const holidayEvents = jewishHolidays.map(holiday => {
        // Create a proper date object for the holiday
        const dateStr = holiday.gregorian_date;
        const startDate = new Date(dateStr + 'T00:00:00');
        // For all-day events, end date should be the next day (exclusive)
        const endDate = new Date(dateStr + 'T00:00:00');
        endDate.setDate(endDate.getDate() + 1);
        
        return {
          title: holiday.name,
          start: startDate,
          end: endDate,
          allDay: true,
          resource: {
            type: 'jewish-holiday',
            category: holiday.category,
            description: holiday.description,
            data: holiday
          }
        };
      });
      console.log('Added', holidayEvents.length, 'Jewish holidays');
    }
    
    
    return events;
  }, [filteredCalendarEvents, jewishHolidays, currentView]);

  // Handle clicking on a time slot to create a new item
  const handleSelectSlot = useCallback(({ start, end, box }) => {
    setSelectedSlot({ start, end });
    setSelectedEvent(null);
    
    // Position the menu at the click location with smart boundary detection
    if (box) {
      // Use clientX and clientY for actual mouse position
      const clickX = box.clientX || box.x || window.innerWidth / 2;
      const clickY = box.clientY || box.y || window.innerHeight / 2;
      
      // Approximate menu dimensions
      const menuWidth = 280; // Menu width (matches min-w-[280px])
      const menuHeight = 280;
      const bottomPadding = 40;
      const topPadding = 10; // Space between click point and menu
      const sidePadding = 20; // Padding from screen edges
      
      // Check if menu would go off bottom of screen
      const wouldGoOffBottom = clickY + menuHeight + bottomPadding > window.innerHeight;
      
      // Check if menu would go off right side (since it's centered, check half width from click point)
      const menuHalfWidth = menuWidth / 2;
      const wouldGoOffRight = clickX + menuHalfWidth + sidePadding > window.innerWidth;
      const wouldGoOffLeft = clickX - menuHalfWidth < sidePadding;
      
      // Calculate the actual X position
      let x;
      if (wouldGoOffRight) {
        // Position menu to the left, aligned to right edge
        x = window.innerWidth - sidePadding - menuHalfWidth;
      } else if (wouldGoOffLeft) {
        // Position menu to the right, aligned to left edge
        x = sidePadding + menuHalfWidth;
      } else {
        // Center on click point
        x = clickX;
      }
      
      // Calculate the actual Y position based on whether menu opens up or down
      let y;
      if (wouldGoOffBottom) {
        // Position menu above the click point
        y = clickY - menuHeight - topPadding;
        // Make sure we don't go off the top of the screen
        if (y < 20) {
          y = 20;
        }
      } else {
        // Position menu below the click point
        y = clickY + topPadding;
      }
      
      setCreateMenuPosition({ 
        x, 
        y,
        openUpwards: wouldGoOffBottom
      });
    } else {
      // Fallback to center of screen if box info not available
      setCreateMenuPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
        openUpwards: false
      });
    }
    
    setShowCreateMenu(true);
  }, []);

  // Handle menu option selection
  const handleCreateMenuSelect = useCallback((type) => {
    setShowCreateMenu(false);
    
    switch(type) {
      case 'event':
        setShowCreateEventModal(true);
        break;
      case 'class':
        setShowCreateClassModal(true);
        break;
      case 'other':
        setShowCreateModal(true);
        break;
      default:
        break;
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showCreateMenu) return;

    const handleClickOutside = (e) => {
      // Check if click is outside the menu
      const menuElement = document.querySelector('[data-calendar-create-menu]');
      if (menuElement && !menuElement.contains(e.target)) {
        setShowCreateMenu(false);
      }
    };

    // Add slight delay to prevent immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateMenu]);

  // Handle clicking on an existing event
  const handleSelectEvent = useCallback((event) => {
    // Don't allow interacting with Jewish holidays - they're read-only
    if (event.resource?.type === 'jewish-holiday') {
      // Optionally could show a tooltip or info modal about the holiday
      return;
    }
    
    // Navigate to detail pages for events and classes
    if (event.resource?.type === 'event' && event.resource?.data?.id) {
      navigate(`/group/${groupId}/events/${event.resource.data.id}`, {
        state: { fromCalendar: true }
      });
      return;
    }
    
    if (event.resource?.type === 'class' && event.resource?.data?.id) {
      navigate(`/group/${groupId}/classes/${event.resource.data.id}`, {
        state: { fromCalendar: true }
      });
      return;
    }
    
    // Don't allow editing prayer times from the calendar
    if (event.resource?.type === 'prayer') {
      // Optionally show a message or navigate to prayer times section
      return;
    }
    
    // For other items, show edit modal
    setSelectedEvent(event);
    setSelectedSlot(null);
    setShowCreateModal(true);
  }, [navigate, groupId]);

  // Custom event style getter
  const eventStyleGetter = useCallback((event) => {
    // Special styling for Jewish holidays
    if (event.resource?.type === 'jewish-holiday') {
      const category = event.resource?.category || 'minor';
      const categoryColors = {
        'major': { bg: '#9333ea', border: '#7e22ce' },      // Purple for major holidays
        'minor': { bg: '#059669', border: '#047857' },      // Green for minor holidays
        'fast': { bg: '#dc2626', border: '#b91c1c' },       // Red for fast days
        'modern': { bg: '#0284c7', border: '#0369a1' },     // Blue for modern holidays
        'rosh_chodesh': { bg: '#6366f1', border: '#4f46e5' } // Indigo for Rosh Chodesh
      };
      
      const colors = categoryColors[category] || categoryColors.minor;
      
      return {
        style: {
          backgroundColor: colors.bg,
          borderLeft: `4px solid ${colors.border}`,
          borderRadius: '4px',
          opacity: 0.85,
          color: 'white',
          border: `1px solid ${colors.border}`,
          display: 'block',
          fontSize: '12px',
          fontWeight: '600',
          fontStyle: 'italic',
          padding: '2px 4px'
        }
      };
    }
    
    // Default styling for regular calendar events
    const backgroundColor = event.resource?.color || '#3b82f6';
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '13px',
        fontWeight: '500'
      }
    };
  }, []);

  // Handle create dropdown actions
  const handleCreateEvent = () => {
    setShowCreateDropdown(false);
    setShowCreateEventModal(true);
  };

  const handleCreateClass = () => {
    setShowCreateDropdown(false);
    setShowCreateClassModal(true);
  };

  const handleCreateOther = () => {
    setShowCreateDropdown(false);
    // Set a slot with current time to initialize the form
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    setSelectedSlot({ start: now, end: oneHourLater });
    setSelectedEvent(null);
    setCreateModalInitialStep('other'); // Go directly to 'other' step
    setShowCreateModal(true);
  };

  // Handle Google Calendar import
  const handleGoogleCalendarImport = () => {
    setShowGoogleCalendarModal(true);
  };

  // Handle events imported from Google Calendar
  const handleEventsImported = async (results) => {
    console.log('Events imported, refreshing calendar in background...', results);
    
    // Refresh calendar data in the background while modal shows "Complete" screen
    // Don't await - let it refresh while user sees the success message
    fetchCalendarData();
    
    // Show success toast immediately
    if (window.showToast) {
      const message = results.duplicates > 0
        ? `Successfully imported ${results.imported} event${results.imported !== 1 ? 's' : ''}! ${results.duplicates} duplicate${results.duplicates !== 1 ? 's' : ''} skipped.`
        : `Successfully imported ${results.imported} event${results.imported !== 1 ? 's' : ''}!`;
      
      window.showToast(message, 'success');
    }
  };

  // Handle modal close after import
  const handleImportModalClose = () => {
    setShowGoogleCalendarModal(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the create dropdown
      if (showCreateDropdown) {
        const createButton = event.target.closest('button');
        const dropdownMenu = event.target.closest('.absolute.right-0.top-full');
        const isCreateButton = createButton?.textContent?.includes('Create');
        
        if (!isCreateButton && !dropdownMenu) {
          setShowCreateDropdown(false);
        }
      }
      
      if (openCalendarDropdownId && !event.target.closest('.calendar-chip-container')) {
        setOpenCalendarDropdownId(null);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showDeleteConfirmModal) {
          cancelDeleteCalendar();
        }
        if (showCreateDropdown) {
          setShowCreateDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showCreateDropdown, openCalendarDropdownId, showDeleteConfirmModal]);

  // Handle calendar dropdown toggle
  const handleCalendarDropdownToggle = (calendarId, e) => {
    e.stopPropagation();
    setOpenCalendarDropdownId(openCalendarDropdownId === calendarId ? null : calendarId);
  };

  // Handle edit calendar
  const handleEditCalendar = (calendar) => {
    setEditingCalendar(calendar);
    setShowEditCalendarModal(true);
    setOpenCalendarDropdownId(null);
  };

  // Handle delete calendar
  const handleDeleteCalendar = (calendar) => {
    if (calendar.is_default) {
      alert('Cannot delete the default calendar');
      return;
    }
    
    setCalendarToDelete(calendar);
    setShowDeleteConfirmModal(true);
    setOpenCalendarDropdownId(null);
  };

  // Confirm delete calendar
  const confirmDeleteCalendar = async () => {
    if (!calendarToDelete) return;

    try {
      await calendarApi.deleteCalendar(groupId, calendarToDelete.id);
      
      // Update the calendar list immediately
      setCalendars(prev => prev.filter(cal => cal.id !== calendarToDelete.id));
      
      // If deleted calendar was active, remove it from active calendars
      if (activeCalendars.has(calendarToDelete.id)) {
        const newActive = new Set(activeCalendars);
        newActive.delete(calendarToDelete.id);
        
        // If no calendars left, add the default one
        if (newActive.size === 0) {
          const defaultCal = calendars.find(c => c.is_default && c.id !== calendarToDelete.id);
          if (defaultCal) newActive.add(defaultCal.id);
          else {
            const remainingCals = calendars.filter(c => c.id !== calendarToDelete.id);
            if (remainingCals.length > 0) newActive.add(remainingCals[0].id);
          }
        }
        
        setActiveCalendars(newActive);
      }
      
      if (window.showToast) {
        window.showToast(`Calendar "${calendarToDelete.name}" deleted successfully`, 'success');
      }
      
      // Refresh calendars in the background to ensure sync
      await fetchCalendars();
    } catch (err) {
      console.error('Error deleting calendar:', err);
      if (window.showToast) {
        window.showToast(err.response?.data?.error || 'Failed to delete calendar', 'error');
      }
    } finally {
      setShowDeleteConfirmModal(false);
      setCalendarToDelete(null);
    }
  };

  // Cancel delete calendar
  const cancelDeleteCalendar = () => {
    setShowDeleteConfirmModal(false);
    setCalendarToDelete(null);
  };

  // Custom date header format for week view
  const customDateHeader = ({ date, label, isOffRange }) => {
    const dayName = moment(date).format('ddd').toUpperCase();
    const dayNumber = moment(date).format('D');
    const isToday = moment(date).isSame(moment(), 'day');
    
    return (
      <div className={`custom-date-header ${isToday ? 'today' : ''}`}>
        <div className="day-name">{dayName}</div>
        <div className="day-number">{dayNumber}</div>
      </div>
    );
  };

  // Custom month date cell wrapper to display Jewish holidays
  const MonthDateCellWrapper = ({ value, children }) => {
    const holidays = getHolidaysForDate(value);
    
    // Debug logging - only log when there are holidays
    if (holidays.length > 0) {
      console.log(`🎉 Rendering ${holidays.length} holiday(s) for ${value.toISOString().split('T')[0]}:`, holidays);
    }
    
    return (
      <div className="rbc-day-bg">
        {children}
        {holidays.length > 0 && (
          <div className="jewish-holidays-in-cell">
            {holidays.map((holiday, idx) => {
              console.log(`  - Creating holiday element: ${holiday.name} (${holiday.category})`);
              return (
                <div 
                  key={idx}
                  className={`jewish-holiday holiday-${holiday.category}`}
                  title={`${holiday.name}${holiday.description ? ': ' + holiday.description : ''}`}
                >
                  <span className="holiday-indicator">•</span>
                  <span className="holiday-name">{holiday.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Custom Popup component with better positioning
  const CustomPopup = ({ events, position, ...props }) => {
    const popupRef = React.useRef(null);
    const [adjustedPosition, setAdjustedPosition] = useState(position);

    useEffect(() => {
      if (popupRef.current && position) {
        const popup = popupRef.current;
        const rect = popup.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        let { x, y } = position;
        
        // Adjust horizontal position if popup would go off-screen
        if (x + rect.width > viewportWidth - 20) {
          x = viewportWidth - rect.width - 20;
        }
        if (x < 20) {
          x = 20;
        }
        
        // Adjust vertical position if popup would go off-screen
        if (y + rect.height > viewportHeight - 20) {
          y = viewportHeight - rect.height - 20;
        }
        if (y < 20) {
          y = 20;
        }
        
        setAdjustedPosition({ x, y });
      }
    }, [position]);

    return (
      <div
        ref={popupRef}
        className="rbc-overlay"
        style={{
          left: adjustedPosition.x,
          top: adjustedPosition.y
        }}
      >
        <div className="rbc-overlay-header">
          {moment(events[0]?.start).format('MMMM D, YYYY')}
        </div>
        {events.map((event, idx) => (
          <div
            key={idx}
            className="rbc-event"
            style={{
              backgroundColor: event.resource?.color || '#1967d2',
              marginBottom: '4px',
              padding: '4px 8px',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '13px',
              cursor: 'pointer'
            }}
            onClick={() => {
              if (props.onSelectEvent) {
                props.onSelectEvent(event);
              }
            }}
          >
            {event.title}
          </div>
        ))}
      </div>
    );
  };

  // Custom toolbar matching the design inspiration
  const CustomToolbar = ({ date, onNavigate, onView, view }) => {
    const goToBack = () => onNavigate('PREV');
    const goToNext = () => onNavigate('NEXT');
    const goToToday = () => onNavigate('TODAY');

    const viewNamesGroup = useMemo(() => [
      { view: 'day', label: 'Day' },
      { view: 'week', label: 'Week' },
      { view: 'month', label: 'Month' }
    ], []);

    // Format date display based on view
    const getDateDisplay = () => {
      if (view === 'month') {
        return moment(date).format('MMMM YYYY');
      } else if (view === 'week') {
        const start = moment(date).startOf('week');
        const end = moment(date).endOf('week');
        if (start.month() === end.month()) {
          return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
        }
        return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
      } else if (view === 'day') {
        return moment(date).format('MMMM D, YYYY');
      }
      return moment(date).format('YYYY');
    };

    return (
      <div className="calendar-toolbar-wrapper">
        {/* Single Row Header with View Switcher and Navigation */}
        <div className="calendar-single-header">
          {/* Left: Navigation */}
          <div className="header-left-section">
            <button onClick={goToToday} className="today-btn">
              Today
            </button>
            <button onClick={goToBack} className="nav-arrow-btn" aria-label="Previous">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <h2 className="current-date">{getDateDisplay()}</h2>
            <button onClick={goToNext} className="nav-arrow-btn" aria-label="Next">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          {/* Center: View Switcher */}
          <div className="header-center-section">
            {viewNamesGroup.map(({ view: viewName, label }) => (
              <button
                key={viewName}
                className={`view-tab ${view === viewName ? 'active' : ''}`}
                onClick={() => onView(viewName)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Right: Filter Button */}
          <div className="header-right-section">
            <button 
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`relative today-btn ${
                selectedHolidayFilters.length > 0 && selectedHolidayFilters.length < 6
                  ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200'
                  : ''
              }`}
              aria-label="Filter holidays"
              title={`${selectedHolidayFilters.length} holiday types selected`}
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Filters
              {selectedHolidayFilters.length > 0 && selectedHolidayFilters.length < 6 && (
                <span className="absolute -top-1 -right-1 px-1.5 min-w-[1.25rem] h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {selectedHolidayFilters.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleCreateCalendar = async (calendarData) => {
    // Add the new calendar to the list immediately
    setCalendars(prev => {
      const newCalendars = [...prev, calendarData];
      // Sort: default first, then by display_order
      return newCalendars.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return (a.display_order || 0) - (b.display_order || 0);
      });
    });
    
    // Add the new calendar to active calendars
    setActiveCalendars(prev => new Set([...prev, calendarData.id]));
    
    // Close the modal
    setShowCreateCalendarModal(false);
    
    // Refresh calendars in the background to ensure sync
    await fetchCalendars();
  };

  const handleCalendarUpdated = async (updatedCalendar) => {
    // Update the calendar in the list immediately
    setCalendars(prev => {
      const newCalendars = prev.map(cal => 
        cal.id === updatedCalendar.id ? updatedCalendar : cal
      );
      // Sort: default first, then by display_order
      return newCalendars.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return (a.display_order || 0) - (b.display_order || 0);
      });
    });
    
    // Close the edit modal
    setShowEditCalendarModal(false);
    setEditingCalendar(null);
    
    // Refresh calendars in the background to ensure sync
    await fetchCalendars();
  };

  return (
    <div className="calendar-page">
      <div className="calendar-container">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-top">
            <h1>Calendars</h1>
            
            {/* Global Create Button */}
            {isAdmin && (
              <div className="relative">
                <button 
                  className="inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg transition-all duration-200 hover:-translate-y-px shadow-sm hover:shadow-md"
                  onClick={() => setShowCreateDropdown(!showCreateDropdown)}
                >
                  <span className="text-base font-semibold leading-none">+</span>
                  <span className="text-sm">Create</span>
                  <span className="text-[9px] ml-0.5 leading-none">▼</span>
                </button>
                
                {showCreateDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-[1000] min-w-[140px]">
                    <button 
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                      onClick={handleCreateEvent}
                    >
                      Event
                    </button>
                    <button 
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                      onClick={handleCreateClass}
                    >
                      Class
                    </button>
                    <button 
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                      onClick={handleCreateOther}
                    >
                      Other
                    </button>
                    {ENABLE_GOOGLE_CALENDAR_IMPORT && (
                      <button 
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                        onClick={handleGoogleCalendarImport}
                      >
                        <span>Import</span>
                        <FontAwesomeIcon icon={faGoogle} className="text-blue-500 text-base ml-2" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Calendar Tabs */}
          <div className="financials-tabs">
            {calendars.map((calendar) => (
              <div key={calendar.id} className="calendar-chip-container">
                <button
                  className={`tab-button ${activeCalendars.has(calendar.id) ? 'active' : ''} ${isAdmin && !calendar.is_default ? 'calendar-chip-with-menu' : ''}`}
                  onClick={() => {
                    const newActive = new Set(activeCalendars);
                    if (newActive.has(calendar.id)) {
                      // Allow deselecting any calendar, even if it's the only one
                      newActive.delete(calendar.id);
                    } else {
                      newActive.add(calendar.id);
                    }
                    setActiveCalendars(newActive);
                  }}
                >
                  {calendar.icon && <span>{calendar.icon}</span>}
                  <span>{calendar.name}</span>
                  {isAdmin && !calendar.is_default && (
                    <div className="calendar-chip-menu-container">
                      <div
                        className="calendar-chip-menu-button"
                        onClick={(e) => handleCalendarDropdownToggle(calendar.id, e)}
                        title="More options"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleCalendarDropdownToggle(calendar.id, e);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </div>
                      {openCalendarDropdownId === calendar.id && (
                        <div className="calendar-chip-dropdown-menu">
                          <button
                            className="calendar-chip-dropdown-item"
                            onClick={() => handleEditCalendar(calendar)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                            Edit
                          </button>
                          <button
                            className="calendar-chip-dropdown-item delete"
                            onClick={() => handleDeleteCalendar(calendar)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </button>
              </div>
            ))}
            
            {/* Plus Button for New Calendar */}
            {isAdmin && (
              <button
                className="tab-button new-tab-button"
                onClick={() => setShowCreateCalendarModal(true)}
                title="Create New Calendar"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
          </div>
        </div>

        {/* Holiday Filter Slide-in Panel */}
        {showFilterPanel && (
          <>
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
              onClick={() => setShowFilterPanel(false)}
            />
            
            {/* Slide-in Panel */}
            <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-[70] transform translate-x-0 transition-transform duration-300 ease-out">
              <div className="flex flex-col h-full">
                {/* Panel Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Holiday Filters</h3>
                  <button
                    onClick={() => setShowFilterPanel(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Quick Actions */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex gap-2">
                  <button
                    onClick={selectAllFilters}
                    className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearAllFilters}
                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                
                {/* Filter Options - Checkboxes */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2">
                    {[
                      { value: 'major', label: 'Major Holidays', emoji: '🕎' },
                      { value: 'minor', label: 'Minor Holidays', emoji: '📖' },
                      { value: 'fast', label: 'Fast Days', emoji: '🙏' },
                      { value: 'rosh_chodesh', label: 'Rosh Chodesh', emoji: '🌙' },
                      { value: 'shabbat', label: 'Special Shabbats', emoji: '🕯️' },
                      { value: 'modern', label: 'Israeli Holidays', emoji: '🇮🇱' }
                    ].map(({ value, label, emoji }) => (
                      <label
                        key={value}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                          selectedHolidayFilters.includes(value)
                            ? 'bg-amber-50 border-2 border-amber-400'
                            : 'bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedHolidayFilters.includes(value)}
                          onChange={() => toggleHolidayFilter(value)}
                          className="w-5 h-5 text-amber-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-amber-500 cursor-pointer"
                        />
                        <span className="text-xl">{emoji}</span>
                        <span className={`font-medium text-sm ${selectedHolidayFilters.includes(value) ? 'text-amber-700' : 'text-gray-700'}`}>
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-white">
                  <button
                    onClick={() => setShowFilterPanel(false)}
                    className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Calendar Content */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-red-500">{error}</div>
          </div>
        ) : (
          <div className="community-calendar-container">
            <Calendar
              localizer={localizer}
              events={allCalendarEvents}
              startAccessor="start"
              endAccessor="end"
              allDayAccessor="allDay"
              style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable={isAdmin}
              view={currentView}
              onView={setCurrentView}
              date={currentDate}
              onNavigate={setCurrentDate}
              eventPropGetter={eventStyleGetter}
              components={{
                toolbar: CustomToolbar,
                popup: CustomPopup,
                month: {
                  dateCellWrapper: MonthDateCellWrapper
                },
                week: {
                  header: customDateHeader
                },
                day: {
                  header: customDateHeader
                }
              }}
              step={60}
              timeslots={1}
              showMultiDayTimes
              defaultDate={new Date()}
              views={['month', 'week', 'day']}
              min={new Date(2000, 0, 1, 0, 0, 0)}
              max={new Date(2000, 0, 1, 23, 59, 59)}
              popup
              scrollToTime={new Date(2000, 0, 1, 6, 0, 0)}
            />
          </div>
        )}

        {/* Create/Edit Calendar Item Modal */}
        {/* Create Item Type Selection Menu */}
        {showCreateMenu && (
          <div 
            data-calendar-create-menu
            style={{
              position: 'fixed',
              left: `${createMenuPosition.x}px`,
              top: `${createMenuPosition.y}px`,
              zIndex: 1000,
              transform: 'translateX(-50%)', // Only center horizontally
              willChange: 'opacity'
            }}
            className="opacity-0 animate-[fadeIn_0.15s_ease-out_forwards]"
          >
            <div className="bg-white rounded-lg shadow-xl overflow-hidden min-w-[280px] border border-gray-200">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Create New</span>
              </div>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-150 text-left border-b border-gray-100"
                onClick={() => handleCreateMenuSelect('event')}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white shadow-md hover:scale-110 transition-transform duration-150">
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-800">Event</div>
                  <div className="text-xs text-gray-500">Community events, programs</div>
                </div>
              </button>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-150 text-left border-b border-gray-100"
                onClick={() => handleCreateMenuSelect('class')}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white shadow-md hover:scale-110 transition-transform duration-150">
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-800">Class</div>
                  <div className="text-xs text-gray-500">Torah classes, lectures</div>
                </div>
              </button>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-150 text-left"
                onClick={() => handleCreateMenuSelect('other')}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-md hover:scale-110 transition-transform duration-150">
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-800">Other Item</div>
                  <div className="text-xs text-gray-500">Custom calendar item</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {showCreateModal && (
          <CreateCalendarItemModal
            isOpen={showCreateModal}
            onClose={() => {
              setShowCreateModal(false);
              setSelectedSlot(null);
              setSelectedEvent(null);
              setCreateModalInitialStep('choose'); // Reset for next time
            }}
            groupId={groupId}
            selectedSlot={selectedSlot}
            selectedEvent={selectedEvent}
            onItemCreated={() => {
              console.log('📅 onItemCreated callback - closing modal and refreshing');
              setShowCreateModal(false);
              setSelectedSlot(null);
              setSelectedEvent(null);
              fetchCalendarData(); // Refresh calendar data
            }}
            onItemUpdated={() => {
              console.log('📅 onItemUpdated callback - closing modal and refreshing');
              setShowCreateModal(false);
              setSelectedSlot(null);
              setSelectedEvent(null);
              fetchCalendarData(); // Refresh calendar data
            }}
            onItemDeleted={() => {
              console.log('📅 onItemDeleted callback - closing modal and refreshing');
              setShowCreateModal(false);
              setSelectedSlot(null);
              setSelectedEvent(null);
              fetchCalendarData(); // Refresh calendar data
            }}
            calendars={calendars}
            activeCalendar={Array.from(activeCalendars)[0] || null}
            initialStep={createModalInitialStep}
          />
        )}

        {/* Create Event Modal */}
        {showCreateEventModal && (
          <CreateEventModal
            isOpen={showCreateEventModal}
            onClose={() => {
              setShowCreateEventModal(false);
              setSelectedSlot(null);
            }}
            groupId={groupId}
            calendars={calendars}
            activeCalendar={Array.from(activeCalendars)[0] || null}
            selectedSlot={selectedSlot}
            onEventCreated={(event) => {
              setShowCreateEventModal(false);
              setSelectedSlot(null);
              fetchCalendarData(); // Refresh calendar data
            }}
            onEventUpdated={(event) => {
              setShowCreateEventModal(false);
              setSelectedSlot(null);
              fetchCalendarData(); // Refresh calendar data
            }}
          />
        )}

        {/* Create Class Modal */}
        {showCreateClassModal && (
          <CreateClassModal
            isOpen={showCreateClassModal}
            onClose={() => {
              setShowCreateClassModal(false);
              setSelectedSlot(null);
            }}
            groupId={groupId}
            calendars={calendars}
            activeCalendar={Array.from(activeCalendars)[0] || null}
            calendarsLoading={loading}
            selectedSlot={selectedSlot}
            onClassCreated={(classItem) => {
              setShowCreateClassModal(false);
              setSelectedSlot(null);
              fetchCalendarData(); // Refresh calendar data
            }}
            onClassUpdated={(classItem) => {
              setShowCreateClassModal(false);
              setSelectedSlot(null);
              fetchCalendarData(); // Refresh calendar data
            }}
          />
        )}

        {/* Create Calendar Modal */}
        {showCreateCalendarModal && (
          <CreateCalendarModal
            isOpen={showCreateCalendarModal}
            onClose={() => setShowCreateCalendarModal(false)}
            groupId={groupId}
            onCalendarCreated={handleCreateCalendar}
          />
        )}

        {/* Edit Calendar Modal */}
        {showEditCalendarModal && (
          <EditCalendarModal
            isOpen={showEditCalendarModal}
            onClose={() => {
              setShowEditCalendarModal(false);
              setEditingCalendar(null);
            }}
            groupId={groupId}
            calendar={editingCalendar}
            onCalendarUpdated={handleCalendarUpdated}
          />
        )}

        {/* Google Calendar Import Modal */}
        {showGoogleCalendarModal && (
          <GoogleCalendarImport
            isOpen={showGoogleCalendarModal}
            onClose={handleImportModalClose}
            groupId={groupId}
            onEventsImported={handleEventsImported}
            calendars={calendars}
            activeCalendar={Array.from(activeCalendars)[0] || null}
          />
        )}

        {/* Delete Confirmation Modal - Tailwind CSS */}
        {showDeleteConfirmModal && calendarToDelete && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                aria-hidden="true"
                onClick={cancelDeleteCalendar}
              ></div>

              {/* Center modal */}
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              {/* Modal panel */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    {/* Icon */}
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    {/* Content */}
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Delete Calendar
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete <span className="font-semibold text-gray-700">"{calendarToDelete.name}"</span>? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Actions */}
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={confirmDeleteCalendar}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={cancelDeleteCalendar}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityCalendar;
